/**
 * Utilitários para compartilhamento de conteúdo
 */

/**
 * Detecta as capacidades de compartilhamento do navegador
 * @returns {Object} Objeto com informações sobre capacidades de compartilhamento
 */
export const detectShareCapabilities = () => {
    const canShare = navigator.share && typeof navigator.share === 'function';
    const canShareFiles = canShare && navigator.canShare && navigator.canShare({ files: [] });
    const hasClipboard = navigator.clipboard && navigator.clipboard.writeText;
    console.log('canShareFiles',canShareFiles)
    // Detecta se é mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Detecta o navegador
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isEdge = /Edg/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(navigator.userAgent);

    return {
        canShare,
        canShareFiles,
        hasClipboard,
        isMobile,
        isChrome,
        isEdge,
        isSafari,
        isFirefox,
        platform: isMobile ? 'mobile' : 'desktop'
    };
};

/**
 * Converte base64 para File
 * @param {string} base64Data - Dados em base64
 * @param {string} filename - Nome do arquivo
 * @returns {File|null} Objeto File ou null se houver erro
 */
export const base64ToFile = (base64Data, filename) => {
    try {
        const arr = base64Data.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
        const bstr = atob(arr[1] || base64Data);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    } catch (error) {
        console.error('Erro ao converter base64 para arquivo:', error);
        return null;
    }
};

/**
 * Copia texto para a área de transferência
 * @param {string} text - Texto para copiar
 * @returns {Promise<boolean>} Promise que resolve com true se copiou com sucesso
 */
export const copyToClipboard = async (text) => {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        } else {
            // Fallback para navegadores mais antigos
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const result = document.execCommand('copy');
            document.body.removeChild(textArea);
            return result;
        }
    } catch (error) {
        console.error('Erro ao copiar para clipboard:', error);
        return false;
    }
};

/**
 * Abre URL em nova aba com verificações de segurança
 * @param {string} url - URL para abrir
 * @param {string} windowFeatures - Características da janela
 * @returns {Window|null} Referência da janela ou null
 */
export const openSecureWindow = (url, windowFeatures = 'noopener,noreferrer') => {
    try {
        return window.open(url, '_blank', windowFeatures);
    } catch (error) {
        console.error('Erro ao abrir janela:', error);
        return null;
    }
};

/**
 * Cria uma mensagem de WhatsApp formatada
 * @param {string} text - Texto da mensagem
 * @param {string} phone - Número de telefone (opcional)
 * @returns {string} URL do WhatsApp
 */
export const createWhatsAppUrl = (text, phone = '') => {
    const encodedText = encodeURIComponent(text);
    const baseUrl = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
    return `${baseUrl}?text=${encodedText}`;
};

/**
 * Sanitiza nome de arquivo removendo caracteres especiais
 * @param {string} filename - Nome do arquivo
 * @returns {string} Nome sanitizado
 */
export const sanitizeFilename = (filename) => {
    return filename.replace(/[^a-zA-Z0-9\-_.]/g, '-').replace(/-+/g, '-');
};

/**
 * Função principal de compartilhamento robusto
 * @param {Object} options - Opções de compartilhamento
 * @param {string} options.title - Título do compartilhamento
 * @param {string} options.text - Texto para compartilhar
 * @param {string} options.url - URL para compartilhar (opcional)
 * @param {string} options.imageBase64 - Imagem em base64 (opcional)
 * @param {string} options.imageName - Nome da imagem (opcional)
 * @param {boolean} options.askForImage - Se deve perguntar sobre compartilhar imagem
 * @param {Function} options.onSuccess - Callback de sucesso
 * @param {Function} options.onError - Callback de erro
 */
export const robustShare = async (options) => {
    const {
        title,
        text,
        url,
        imageBase64,
        imageName,
        askForImage = true,
        onSuccess,
        onError
    } = options;

    const capabilities = detectShareCapabilities();
    
    // Estratégia de compartilhamento baseada nas capacidades
    if (capabilities.canShare) {
        try {
            // Compartilha texto primeiro
            const shareData = { title, text };
            // if (url) shareData.url = url;

            await navigator.share(shareData);
            
            if (onSuccess) onSuccess('text');
            console.log('Texto compartilhado com sucesso via navigator.share');

            // Se tem imagem e suporta arquivos
            if (imageBase64 && capabilities.canShareFiles && askForImage) {
                setTimeout(async () => {
                    const shouldShareImage = window.confirm(
                        '✅ Texto compartilhado!\n\n🖼️ Deseja compartilhar a imagem também?'
                    );
                    
                    if (shouldShareImage) {
                        try {
                            const filename = sanitizeFilename(imageName || 'imagem.jpg');
                            const imageFile = base64ToFile(imageBase64, filename);
                            
                            if (imageFile && navigator.canShare({ files: [imageFile] })) {
                                await navigator.share({
                                    title: `Imagem - ${title}`,
                                    files: [imageFile]
                                });
                                if (onSuccess) onSuccess('image');
                                console.log('Imagem compartilhada com sucesso');
                            }
                        } catch (imageError) {
                            console.log('Erro ao compartilhar imagem:', imageError);
                            if (imageError.name !== 'AbortError' && onError) {
                                onError(imageError, 'image');
                            }
                        }
                    }
                }, 1000);
            }
            
            return true;
            
        } catch (error) {
            console.log('Erro no navigator.share:', error);
            if (error.name === 'AbortError') {
                return false; // Usuário cancelou
            }
            // Continua para fallback
        }
    }

    // Fallback para WhatsApp
    try {
        const whatsappUrl = createWhatsAppUrl(text);
        const whatsappWindow = openSecureWindow(whatsappUrl);
        
        if (whatsappWindow) {
            if (onSuccess) onSuccess('whatsapp');
            return true;
        } else {
            throw new Error('Não foi possível abrir o WhatsApp');
        }
    } catch (fallbackError) {
        console.error('Erro no fallback WhatsApp:', fallbackError);
        
        // Último recurso: copiar para clipboard
        const copied = await copyToClipboard(text);
        if (copied) {
            alert('📋 Não foi possível abrir o WhatsApp.\nO texto foi copiado para a área de transferência!');
            if (onSuccess) onSuccess('clipboard');
            return true;
        } else {
            const errorMsg = url 
                ? `❌ Não foi possível compartilhar automaticamente.\nPor favor, copie o link manualmente:\n\n${url}`
                : '❌ Não foi possível compartilhar o conteúdo.';
            
            alert(errorMsg);
            if (onError) onError(new Error('Falha em todos os métodos de compartilhamento'), 'all');
            return false;
        }
    }
};
